import { getWixVideoPreviewUrls } from '@wix/thumbnail-generator';
import { find, isEmpty, path, pipe, prop, propEq } from 'ramda';
import { isTruthy } from 'ramda-adjunct';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TranscodingStatus } from '../../constants/TranscodingStatus';
import { useMMGRSettings } from '../hoc/withMMGRSettings';
import {
  MediaManagerSDKAnyItem,
  MediaManagerSDKFile,
  MediaManagerSDKFileOutputAudio,
  MediaManagerSDKThreeDPreview,
} from './index';
import { getTranslatedItemName } from './items';

export const useVideoPreviews = (item: MediaManagerSDKFile) => {
  const {
    mmgrSettings: { privateMediaRestVideoFiles },
  } = useMMGRSettings();

  return useMemo(
    () =>
      (!isEmpty(item.videoPreviewUrls)
        ? item.videoPreviewUrls
        : getWixVideoPreviewUrls(item as any, {
            videoFilesUrl: privateMediaRestVideoFiles,
            videoPreviewQuality: '480p',
          })) || [],
    [item, privateMediaRestVideoFiles],
  );
};

export const useFileOutputPreview = (item: MediaManagerSDKFile) => {
  return useMemo<[status: TranscodingStatus, url: string]>(() => {
    if (!item) {
      return [null, null];
    }
    const filePreview = path(['fileOutput', 'preview', '0'], item) as MediaManagerSDKThreeDPreview;
    if (!filePreview || filePreview.status !== TranscodingStatus.Ready) {
      return [prop('status', filePreview), null];
    }

    return [prop('status', filePreview), prop('url', filePreview)];
  }, [item]);
};

export const useMusicPreview = (item: MediaManagerSDKFile) => {
  const {
    mmgrSettings: { privateMediaRestFiles },
  } = useMMGRSettings();

  return useMemo(() => {
    if (!item) {
      return null;
    }

    const fileOutput = path<MediaManagerSDKFileOutputAudio[]>(['fileOutput', 'audio'], item);
    if (!fileOutput) {
      return item.originalUrl;
    }

    const preview = find<MediaManagerSDKFileOutputAudio>(propEq('tag', 'preview'), fileOutput);
    if (preview) {
      return `${privateMediaRestFiles}/${preview.url}`;
    }

    return pipe(
      find(pipe(prop('url'), isTruthy)),
      prop<string>('url'),
      (preview) => `${privateMediaRestFiles}/${preview}`,
    )(fileOutput);
  }, [item, privateMediaRestFiles]);
};

export const useTranslatedItemName = (item: MediaManagerSDKAnyItem): string => {
  const { formatMessage } = useIntl();

  return useMemo(() => getTranslatedItemName(item, formatMessage), [item, formatMessage]);
};
